import { useLabelColumnClearance, type ScrollerResolver } from "@/hooks/useHorizontalScrollOffset";

export interface CurrentTimeMarkerPillProps {
  /** Px offset of the marker on the timeline (0 = the start hour). */
  offset: number;
  /** Px from the container's left edge to the timeline's start (label column, padding). */
  gridLeft: number;
  /** The already-formatted time to show, e.g. "2:30 PM". */
  label: string;
  /** Tailwind z-class from the view's layer scale (see lib/scheduleLayers). */
  zClass: string;
  /** The element that scrolls, so the pill can hide before it reaches the labels. */
  resolveScroller: ScrollerResolver;
  /** Padding between that scroll container and the grid (read-only canvas: p-4). */
  canvasPadding?: number;
}

/**
 * The "now" time above the hour axis.
 *
 * It is centred on the marker and sits on the top layer — the one element that
 * would otherwise paint straight over the sticky stage / custom-subject labels
 * once the timeline is scrolled. So, like the hour labels, it hides until it is
 * fully clear of that column, and subscribes to the scroll offset itself rather
 * than making the whole timeline re-render on every frame.
 */
export function CurrentTimeMarkerPill({
  offset,
  gridLeft,
  label,
  zClass,
  resolveScroller,
  canvasPadding = 0,
}: CurrentTimeMarkerPillProps) {
  const firstClearOffset = useLabelColumnClearance(resolveScroller, canvasPadding);
  if (offset < firstClearOffset) return null;

  return (
    <div
      data-testid="current-time-marker-pill"
      className={`pointer-events-none absolute -translate-x-1/2 whitespace-nowrap rounded bg-[#d1ff38] px-2 py-1 text-xs font-semibold text-[#091527] shadow-md ${zClass}`}
      style={{ left: `${gridLeft + offset}px`, top: "4px" }}
    >
      {label}
    </div>
  );
}
